import type { ApercuRadar } from '../api/types';

/**
 * Le cadran de la page d'accueil, reprise de « Radars Home ». Les points ne
 * sont pas décoratifs : il y en a un par tranche d'anneau réellement présente
 * dans le catalogue, placés de façon déterministe à partir du nom du radar —
 * même radar, même dessin d'une visite à l'autre.
 */

const RAYONS: Record<string, [number, number]> = {
  adopt: [8, 32],
  trial: [36, 68],
  assess: [72, 104],
  hold: [108, 140],
};

const COULEURS: Record<string, string> = {
  adopt: '#004F99',
  trial: '#05BAEE',
  assess: '#D6007F',
  hold: '#C4CADF',
};

/** Générateur déterministe : la même graine donne toujours la même figure. */
function suite(graine: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < graine.length; i += 1) {
    h ^= graine.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

const MAX_POINTS_PAR_ANNEAU = 14;

export function CadranRadar({
  apercu,
  teinte,
}: {
  apercu: ApercuRadar;
  teinte: string;
}): JSX.Element {
  const suivant = suite(apercu.radar);

  const points: { x: number; y: number; r: number; couleur: string }[] = [];

  for (const [anneau, [interieur, exterieur]] of Object.entries(RAYONS)) {
    const compte = apercu.parAnneau[anneau] ?? 0;
    if (compte === 0) continue;

    // Un point pour un petit paquet, jusqu'à quatorze : le cadran donne une
    // idée de densité, il ne prétend pas être le radar lui-même.
    const aTracer = Math.min(MAX_POINTS_PAR_ANNEAU, Math.max(1, Math.round(compte / 3)));

    for (let i = 0; i < aTracer; i += 1) {
      const angle = suivant() * Math.PI * 2;
      const rayon = interieur + suivant() * (exterieur - interieur);
      points.push({
        x: 150 + Math.cos(angle) * rayon,
        y: 150 + Math.sin(angle) * rayon,
        r: anneau === 'adopt' ? 4.5 : anneau === 'trial' ? 4 : 3.2,
        couleur: COULEURS[anneau] ?? teinte,
      });
    }
  }

  return (
    <svg viewBox="0 0 300 300" aria-hidden="true" className="cadran">
      <circle cx="150" cy="150" r="140" fill="none" stroke="#E6E8EC" />
      <circle cx="150" cy="150" r="104" fill="none" stroke="#E6E8EC" />
      <circle cx="150" cy="150" r="68" fill="none" stroke="#E6E8EC" />
      <circle cx="150" cy="150" r="32" fill="none" stroke="#C4CADF" />
      <line x1="150" y1="10" x2="150" y2="290" stroke="#EEF0F4" />
      <line x1="10" y1="150" x2="290" y2="150" stroke="#EEF0F4" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={p.r} fill={p.couleur} />
      ))}
    </svg>
  );
}
